import React, { useEffect, useState } from "react";
import AxiosInstance, { IMAGE_BASE_URL } from "../../services/Api";

/**
 * BlobImage — renders an image that may live in Azure Blob Storage
 * (private, streamed through the authenticated API) or in the legacy
 * public uploads folders.
 *
 * Resolution rules for `path`:
 *   - empty                → renders `fallback` (or nothing when absent)
 *   - "http(s)://..."      → used as-is (already a full URL)
 *   - starts with "/"      → legacy static file (/uploads, /public,
 *                            /school_logo, ...) → IMAGE_BASE_URL + path
 *   - anything else        → Azure blob path (e.g. dev/2026015/Upload/
 *                            ContentManagement/172x_photo.png) → fetched
 *                            via GET /content/image with responseType
 *                            "blob" and shown as an object URL
 *
 * Anti-flicker behaviour:
 *   - While a blob is loading, a transparent placeholder is rendered
 *     (same size/className) instead of flashing the fallback and then
 *     swapping — the fallback only appears when loading actually FAILS.
 *   - Fetched object URLs are kept in a small module-level cache, so
 *     re-opening the same message/screen shows the image instantly with
 *     no re-fetch and no flicker. In-flight requests are shared, so ten
 *     rows with the same sender photo trigger ONE download.
 *
 * All other props (alt, style, className, onClick, ...) pass to <img>.
 */

// 1x1 transparent gif — keeps layout stable while the blob loads
const TRANSPARENT_PX =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// path → object URL (string) or in-flight Promise<string>
const blobUrlCache = new Map();
const CACHE_MAX = 150;

const getBlobObjectUrl = (path) => {
  const hit = blobUrlCache.get(path);
  if (hit) return Promise.resolve(hit);

  const request = AxiosInstance.get(
    `/content/image?path=${encodeURIComponent(path)}`,
    { responseType: "blob" },
  )
    .then((response) => {
      const objectUrl = URL.createObjectURL(new Blob([response.data]));
      // bounded cache — evict the oldest resolved entry
      if (blobUrlCache.size >= CACHE_MAX) {
        for (const [key, value] of blobUrlCache) {
          if (typeof value === "string") {
            URL.revokeObjectURL(value);
            blobUrlCache.delete(key);
            break;
          }
        }
      }
      blobUrlCache.set(path, objectUrl);
      return objectUrl;
    })
    .catch((err) => {
      blobUrlCache.delete(path); // allow a retry on next mount
      throw err;
    });

  blobUrlCache.set(path, request);
  return request;
};

// Resolve synchronously when possible so the FIRST paint already has the
// image (cached blob, legacy path, full URL) — no empty→image swap at all.
const resolveSyncSrc = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/")) {
    return `${IMAGE_BASE_URL.replace(/\/$/, "")}${path}`;
  }
  const cached = blobUrlCache.get(path);
  return typeof cached === "string" ? cached : "";
};

const BlobImage = ({ path, fallback = "", alt = "", ...imgProps }) => {
  const [src, setSrc] = useState(() => resolveSyncSrc(path));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);

    const syncSrc = resolveSyncSrc(path);
    if (syncSrc || !path) {
      setSrc(syncSrc);
      return undefined;
    }

    // Azure blob path — fetch through the authenticated API (blob logic).
    // Object URLs are owned by the cache; nothing to revoke on unmount.
    let cancelled = false;

    getBlobObjectUrl(path)
      .then((objectUrl) => {
        if (!cancelled) setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setSrc("");
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  // Still loading a blob path → transparent placeholder (same size via
  // className/style), so the fallback never flashes before the image.
  const loading = Boolean(path) && !src && !failed;
  const finalSrc = src || (loading ? TRANSPARENT_PX : fallback);
  if (!finalSrc) return null;

  return (
    <img
      src={finalSrc}
      alt={alt}
      {...imgProps}
      onError={(e) => {
        if (fallback && e.currentTarget.src !== fallback) {
          e.currentTarget.src = fallback;
        }
        if (imgProps.onError) imgProps.onError(e);
      }}
    />
  );
};

export default BlobImage;
